import type { CostPendingBacklog } from "../lib/types";

interface Props {
  pending?: CostPendingBacklog | null;
}

function formatAge(oldestIso: string | null): string | null {
  if (!oldestIso) return null;
  const oldest = Date.parse(oldestIso);
  if (Number.isNaN(oldest)) return null;
  const minutes = Math.max(0, Math.round((Date.now() - oldest) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/**
 * States the consequence of the cost-record drain lag, not just its size.
 *
 * The LLM branch reaches `llm_cost_records` through the outbox and can trail
 * the event by a long way, while the audit branch inserts directly — so the
 * table looks complete at every point in time and `id` order is not event
 * order. A bare count invites the same misreading; the sentence is the payload.
 */
export default function PendingCostNotice({ pending }: Props) {
  if (!pending || pending.events <= 0) return null;
  const age = formatAge(pending.oldest_created_at);
  return (
    <div
      className="bg-yellow-900/25 border border-yellow-700/50 rounded px-3 py-2 text-sm text-yellow-200"
      role="status"
    >
      <span className="font-medium">
        {pending.events.toLocaleString()} Kostenereignis
        {pending.events === 1 ? "" : "se"} noch nicht verbucht
      </span>
      {age ? <span> (ältestes {age} alt)</span> : null}
      {". "}
      <span className="text-yellow-300/80">
        Die Zahlen unten sind für diesen Zeitraum ein Minimum, keine Gesamtsumme —
        LLM-Kosten werden über die Outbox nachgetragen, Audit-Kosten sofort.
      </span>
    </div>
  );
}
